import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import uniq from 'lodash/uniq';
import _ from 'lodash';

import classNames from 'classnames';
import React, { cloneElement, isValidElement } from 'react';

export function createShorthand(Component, mapValueToProps, val, options = {}) {
  if (typeof Component !== 'function' && typeof Component !== 'string') {
    throw new Error('createShorthand() Component must be a string or function.');
  }
  
  // invariant(isFunction(Component) && isString(Component), 'fuck broww');
  
  // short circuit noop values
  if (isNil(val) || isBoolean(val)) return null;
  
  const isImg = _.get(val, 'props.children.type') === 'img';
  const valIsString = isString(val);
  const valIsNumber = isNumber(val);
  const valIsFunction = isFunction(val);
  const valIsReactElement = isValidElement(val);
  const valIsPropsObject = isPlainObject(val);
  const valIsPrimitiveValue = valIsString || valIsNumber || isArray(val);

  // unhandled type return null
  /* eslint-disable no-console */
  if (!valIsFunction && !valIsReactElement && !valIsPropsObject && !valIsPrimitiveValue) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(
        [
          'Shorthand value must be a string|number|array|object|ReactElement|function.',
          ' Use null|undefined|boolean for none',
          ` Received ${typeof val}.`,
        ].join('')
      );
    }
    return null;
  }
  /* eslint-enable no-console */

  // ----------------------------------------
  // Build up props
  // ----------------------------------------
  const { defaultProps = {} } = options;
  
  // User's props
  const usersProps =
    (valIsReactElement && val.props) ||
    (valIsPropsObject && val) ||
    (valIsPrimitiveValue && mapValueToProps(val));

  // Override props
  let { overrideProps = {} } = options;
  overrideProps = isFunction(overrideProps)
    ? overrideProps({ ...defaultProps, ...usersProps })
    : overrideProps;

  // Merge props
  /* eslint-disable react/prop-types */
  const props = { ...defaultProps, ...usersProps, ...overrideProps };

  // Merge className
  if (defaultProps.className || overrideProps.className || usersProps.className) {
    const mergedClassesNames = classNames(
      defaultProps.className,
      overrideProps.className,
      usersProps.className
    );
    props.className = uniq(mergedClassesNames.split(' ')).join(' ');
  }

  // Merge style
  if (defaultProps.style || overrideProps.style || usersProps.style) {
    props.style = {
      ...defaultProps.style,
      ...usersProps.style,
      ...overrideProps.style,
    };
  }

  // ----------------------------------------
  // Get key
  // ----------------------------------------

  // Use key, childKey, or generate key
  if (isNil(props.key)) {
    const { childKey } = props;
    const { autoGenerateKey = true } = options;

    if (!isNil(childKey)) {
      // apply and consume the childKey
      props.key = typeof childKey === 'function' ? childKey(props) : childKey;
      delete props.childKey;
    } else if (autoGenerateKey && (valIsString || valIsNumber)) {
      // use string/number shorthand values as the key
      props.key = val;
    }
  }

  // ----------------------------------------
  // Create Element
  // ----------------------------------------

  // Clone ReactElements
  if (valIsReactElement && !isImg) return cloneElement(val, props);

  // Create ReactElements from built up props
  if (valIsPrimitiveValue || valIsPropsObject) return <Component {...props} />;

  // Call functions with args similar to createElement()
  if (valIsFunction) return val(Component, props, props.children);
  /* eslint-enable react/prop-types */
}

export function createShorthandFactory(Component, mapValueToProps) {
  if (typeof Component !== 'function' && typeof Component !== 'string') {
    throw new Error('createShorthandFactory() Component must be a string or function.');
  }

  return (val, options) => createShorthand(Component, mapValueToProps, val, options);
}

export const createHTMLInput = createShorthandFactory('input', val => ({
  type: val,
}));
